import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import NewLandingPage from '@/components/NewLandingPage';

// アプリのルートは '/:appId/...' 構成だが、アプリ内の多くの画面が
// navigate('/account') のように appId 無しの絶対パスへ遷移している。
// これらを '/' (静的LP) へ送ると、ログイン済みユーザーが宣伝ページへ飛ばされ、
// 特に「プレミアムへ」(= /account) の導線が機能しなかった。
// → パスをそのまま既定アプリ配下へ付け替えて救済する。
const DEFAULT_APP_ID = 'chusho-app';

const ToDefaultApp: React.FC = () => {
    const location = useLocation();
    return (
        <Navigate
            to={`/${DEFAULT_APP_ID}${location.pathname}${location.search}${location.hash}`}
            replace
        />
    );
};

// src/routes/Router.tsx
const AppSelection = lazy(() => import('@/components/AppSelection'));
const AppHome = lazy(() => import('@/components/AppHome'));
const LandingPage = lazy(() => import('@/components/LandingPage'));
const SubjectSelection = lazy(() => import('@/components/SubjectSelection'));
const YearQuestionSelection = lazy(() => import('@/components/YearQuestionSelection'));
const AdminYearSelection = lazy(() => import('@/components/AdminYearSelection'));
const QuestionPage = lazy(() => import('@/components/QuestionPage'));
const AccountPage = lazy(() => import('@/components/AccountPage'));
const TestPage = lazy(() => import('@/components/TestPage'));
const AdminDashboard = lazy(() => import('@/components/AdminDashboard'));
const QuestionEditor = lazy(() => import('@/components/QuestionEditor'));
const BulkQuestionImport = lazy(() => import('@/components/BulkQuestionImport'));
const SnsSettingsX = lazy(() => import('@/components/SnsSettings/SnsSettingsX'));
const SnsSettingsInstagram = lazy(() => import('@/components/SnsSettings'));
const SubjectCategoryManager = lazy(() => import('@/components/SubjectCategoryManager'));
const SubjectYear = lazy(() => import('@/components/SubjectYear'));
const DiagnosticQuestionManager = lazy(() => import('@/components/DiagnosticQuestionManager'));
const BookmarkList = lazy(() => import('@/components/BookmarkList'));
const MyPostsList = lazy(() => import('@/components/MyPostsList'));
const SettingsPage = lazy(() => import('@/components/SettingsPage'));
const Analytics = lazy(() => import('@/components/Analytics'));
const GA4Analytics = lazy(() => import('@/components/GA4Analytics'));
const AIRoadmapPage = lazy(() => import('@/components/AIRoadmapPage'));
const AIRoadmapTrialPage = lazy(() => import('@/components/AIRoadmapTrialPage'));
const AIRoadmapQuizPage = lazy(() => import('@/components/AIRoadmapQuizPage'));
const AIRoadmapResultsPage = lazy(() => import('@/components/AIRoadmapResultsPage'));
const AIRoadmapProgressPage = lazy(() => import('@/components/AIRoadmapProgressPage'));
const WeakSubjectPracticePage = lazy(() => import('@/components/WeakSubjectPracticePage'));
const PDFViewer = lazy(() => import('@/components/PDFViewer'));
const Tutorial = lazy(() => import('@/components/Tutorial'));
const SecondaryExamSelection = lazy(() => import('@/components/SecondaryExamSelection'));
const SecondarySelfGrade = lazy(() => import('@/components/SecondarySelfGrade'));
const SecondaryExamPractice = lazy(() => import('@/components/SecondaryExamPractice'));
const SecondaryExamHistory = lazy(() => import('@/components/SecondaryExamHistory'));
const SecondaryExamAdmin = lazy(() => import('@/components/SecondaryExamAdmin'));
const NotificationSettings = lazy(() => import('@/components/NotificationSettings'));
const SuperAdminDashboard = lazy(() => import('@/components/SuperAdminDashboard'));
const CommonSettings = lazy(() => import('@/components/CommonSettings'));
const LearnHub = lazy(() => import('@/components/learn/LearnHub'));
const LearnTopicPage = lazy(() => import('@/components/learn/LearnTopicPage'));
const CommunityPage = lazy(() => import('@/components/community/CommunityPage'));
const Pass2jiPage = lazy(() => import('@/components/Pass2jiPage'));
const ThreadPage = lazy(() => import('@/components/community/ThreadPage'));

const RouteFallback: React.FC = () => (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid rgba(228,185,91,0.15)', borderTopColor: '#E4B95B', animation: 'spin 0.9s linear infinite' }} />
        <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
);

const AppRouter: React.FC = () => {
    return (
        <Suspense fallback={<RouteFallback />}>
        <Routes>
            {/* トップページ（新しいランディングページ） */}
            <Route path="/" element={<NewLandingPage />} />

            {/* アプリ選択画面 */}
            <Route path="/apps" element={<AppSelection />} />

            {/* スーパー管理者ダッシュボード */}
            <Route path="/admin" element={<SuperAdminDashboard />} />

            {/* 各アプリのルート */}
            <Route path="/:appId">
                {/* ホーム */}
                <Route index element={<AppHome />} />

                {/* PRページ */}
                <Route path="landing" element={<LandingPage />} />

                {/* 体感型学習（触って学ぶ） */}
                <Route path="learn" element={<LearnHub />} />
                <Route path="learn/:topicId" element={<LearnTopicPage />} />

                {/* 受講生コミュニティ */}
                <Route path="community" element={<CommunityPage />} />
                <Route path="community/:postId" element={<ThreadPage />} />

                {/* 科目選択 */}
                <Route path="subjects" element={<SubjectSelection />} />

                {/* 特定科目の年度選択 */}
                <Route path="subjects/:subjectId" element={<YearQuestionSelection />} />

                {/* 特定科目・年度の問題一覧 */}
                <Route path="subjects/:subjectId/:year" element={<SubjectYear />} />

                {/* ブックマークページ */}
                <Route path="bookmarks" element={<BookmarkList />} />

                {/* 自分の投稿一覧 */}
                <Route path="myposts" element={<MyPostsList />} />

                {/* 特定科目・年度・問題番号の問題ページ */}
                <Route path="questions/:subjectId/:year/:questionNumber" element={<QuestionPage />} />

                {/* アカウントページ */}
                <Route path="account" element={<AccountPage />} />

                {/* 2次直前パスの購入ページ（期間限定・2026-10-12 追加） */}
                <Route path="pass" element={<Pass2jiPage />} />

                {/* テストページ */}
                <Route path="test" element={<TestPage />} />

                {/* 管理者ダッシュボード */}
                <Route path="admin" element={<AdminDashboard />} />

                {/* 管理者用科目選択 */}
                <Route path="admin/subjects/:subjectId" element={<AdminYearSelection />} />

                {/* 管理者用特定科目・年度の質問編集 */}
                <Route path="admin/subjects/:subjectId/:year" element={<QuestionEditor />} />

                {/* 管理者用一括登録 */}
                <Route path="admin/subjects/:subjectId/:year/bulk" element={<BulkQuestionImport />} />

                {/* 共通設定 */}
                <Route path="admin/common-settings" element={<CommonSettings />} />

                {/* SNS投稿設定 */}
                <Route path="admin/sns-settings" element={<SnsSettingsX />} />
                <Route path="admin/sns-settings/x" element={<SnsSettingsX />} />
                <Route path="admin/sns-settings/instagram" element={<SnsSettingsInstagram />} />
                <Route path="admin/categories" element={<SubjectCategoryManager />} />
                <Route path="admin/diagnostic" element={<DiagnosticQuestionManager />} />

                {/* 設定ページ */}
                <Route path="settings" element={<SettingsPage />} />

                {/* 分析（学習データ） */}
                <Route path="analytics" element={<Analytics />} />

                {/* GA4アナリティクス */}
                <Route path="ga4-analytics" element={<GA4Analytics />} />

                {/* AIロードマップ */}
                <Route path="ai-roadmap" element={<AIRoadmapPage />} />
                <Route path="ai-roadmap/trial" element={<AIRoadmapTrialPage />} />
                <Route path="ai-roadmap/trial/quiz/:subject" element={<AIRoadmapQuizPage />} />
                <Route path="ai-roadmap/trial/results" element={<AIRoadmapResultsPage />} />
                <Route path="ai-roadmap/progress" element={<AIRoadmapProgressPage />} />

                {/* チュートリアル */}
                <Route path="tutorial" element={<Tutorial />} />

                {/* 弱点対策 */}
                <Route path="practice/weak-subjects" element={<WeakSubjectPracticePage />} />

                {/* PDFビューワー */}
                <Route path="pdf-viewer" element={<PDFViewer />} />

                {/* 二次試験対策 */}
                <Route path="secondary-exam" element={<SecondaryExamSelection />} />
                {/* 2026-10-01: 年度・事例別の問題が無いので、**サンプルの1画面にまとめる**。
                    年度つきの古いURLも同じ画面へ寄せる（年度を名乗らせない）。 */}
                {/* 自分の答案を貼って採点（年度・事例別の過去問が無くても使える） */}
                <Route path="secondary-exam/self" element={<SecondarySelfGrade />} />
                <Route path="secondary-exam/sample" element={<SecondaryExamPractice />} />
                <Route path="secondary-exam/:year/:examCase" element={<SecondaryExamPractice />} />
                <Route path="secondary-exam/history" element={<SecondaryExamHistory />} />

                {/* 管理者用二次試験登録 */}
                <Route path="admin/secondary-exam" element={<SecondaryExamAdmin />} />

                {/* 通知設定 */}
                <Route path="notifications/settings" element={<NotificationSettings />} />
            </Route>

            {/* appId 無しの絶対パス（/account, /ai-roadmap など）は
                '/' ではなく既定アプリ配下の同じパスへ送る。
                '/' に戻すと、アプリ内のリンクを踏んだログイン済みユーザーが
                宣伝ページに落ちてしまい、課金導線(/account)まで死ぬ。 */}
            <Route path="/subjects" element={<ToDefaultApp />} />
            <Route path="/subjects/*" element={<ToDefaultApp />} />
            <Route path="/bookmarks" element={<ToDefaultApp />} />
            <Route path="/myposts" element={<ToDefaultApp />} />
            <Route path="/account" element={<ToDefaultApp />} />
            <Route path="/pass" element={<ToDefaultApp />} />
            <Route path="/settings" element={<ToDefaultApp />} />
            <Route path="/analytics" element={<ToDefaultApp />} />
            <Route path="/ai-roadmap" element={<ToDefaultApp />} />
            <Route path="/ai-roadmap/*" element={<ToDefaultApp />} />
            <Route path="/practice/*" element={<ToDefaultApp />} />
            {/* 告知用の短いURL。/chusho-app/secondary-exam/self へ送る（2026-10-02） */}
            <Route path="/grade" element={<Navigate to="/chusho-app/secondary-exam/self" replace />} />
            <Route path="/secondary-exam" element={<ToDefaultApp />} />
            <Route path="/secondary-exam/*" element={<ToDefaultApp />} />
            <Route path="/tutorial" element={<ToDefaultApp />} />
            <Route path="/questions/*" element={<ToDefaultApp />} />
            <Route path="/landing" element={<ToDefaultApp />} />
            <Route path="/test" element={<ToDefaultApp />} />
            <Route path="/community" element={<ToDefaultApp />} />
            <Route path="/community/*" element={<ToDefaultApp />} />
            <Route path="/learn" element={<ToDefaultApp />} />
            <Route path="/notifications/*" element={<ToDefaultApp />} />
            <Route path="/ga4-analytics" element={<ToDefaultApp />} />
            {/* 実体の無いパスは従来どおりトップへ */}
            <Route path="/pdf-viewer" element={<Navigate to="/" replace />} />

            {/* その他の不明なパスもトップにリダイレクト */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
    );
};

export default AppRouter;
